/**
 * Derive M3 vehicle (and optional body) SKU segments from PDM `base_part_number`.
 *
 * Runtime evidence (Dato + PDM cars):
 * - `base_part_number` === Dato `vehicle.sku` + body option `sku` (full M3 prefix)
 * - Kit tier (`XS`/`XP`/`XF`) starts immediately after `base_part_number` in `part_number`
 * - Rows SKU was empty in captured samples
 *
 * Rules:
 * - One unique base → put the whole base on `vehicle.sku` (body/rows stay empty)
 * - Several bases that share a prefix → `vehicle.sku` = LCP; each remainder is a body suffix
 */

export type DerivedM3Skus = {
  vehicleSku: string
  /** Non-empty suffixes after LCP when multiple bases share a common prefix. */
  bodySuffixes: string[]
  uniqueBases: string[]
  longestCommonPrefix: string
}

function longestCommonPrefix(values: string[]): string {
  if (values.length === 0) return ""
  let prefix = values[0] ?? ""
  for (let i = 1; i < values.length; i++) {
    const value = values[i] ?? ""
    while (prefix.length > 0 && !value.startsWith(prefix)) {
      prefix = prefix.slice(0, -1)
    }
    if (prefix.length === 0) break
  }
  return prefix
}

export function deriveM3SkusFromPdmBases(
  basePartNumbers: string[]
): DerivedM3Skus {
  const uniqueBases = [
    ...new Set(
      basePartNumbers.map((b) => b.replace(/\s+/g, "").trim()).filter(Boolean)
    ),
  ].sort()

  if (uniqueBases.length === 0) {
    return {
      vehicleSku: "",
      bodySuffixes: [],
      uniqueBases: [],
      longestCommonPrefix: "",
    }
  }

  if (uniqueBases.length === 1) {
    const only = uniqueBases[0] ?? ""
    return {
      vehicleSku: only,
      bodySuffixes: [],
      uniqueBases,
      longestCommonPrefix: only,
    }
  }

  const lcp = longestCommonPrefix(uniqueBases)
  const bodySuffixes = uniqueBases
    .map((base) => (lcp && base.startsWith(lcp) ? base.slice(lcp.length) : ""))
    .filter(Boolean)

  return {
    vehicleSku: lcp,
    bodySuffixes,
    uniqueBases,
    longestCommonPrefix: lcp,
  }
}
